<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, onMounted, ref, watch } from 'vue';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import {
  formatCurrency,
  formatDate,
  formatWithdrawalStatus,
  formatItemValue
} from '@/utils/formatters.ts';
import BankService from "@/services/bank/bankService.ts";
import { WithdrawalStatusKey } from '@/enums/bank_enum.ts';
import { displayToast } from "@/utils/toastUtils.ts";
import { displayModal } from "@/utils/modalUtils.ts";
import OperationStatusComponent from "@/components/bank/OperationStatusComponent.vue";

import type {
  PaginatedQuery,
  SortItem,
  SortOrder
} from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type {
  WithdrawalOperationResponse,
  WithdrawalOperationsSortKey,
  WithdrawalRefuseRequest
} from '@/types/services/bank/bank.type.ts';
import { useDrawer } from '@/stores/useDrawer.ts';
import DetailWithdrawalView from '@/views/bank/withdrawal/DetailWithdrawalView.vue';

const { t } = useI18n();
const { openDrawer } = useDrawer()

const withdrawalOperations = ref<WithdrawalOperationResponse[]>([]);
const loading = ref<boolean>(false);

const search = ref<string>('');
const sort = ref<WithdrawalOperationsSortKey>('createdAt');
const order = ref<SortOrder>('desc');
const limit = ref<number>(10);
const page = ref<number>(1);

const total = ref<number>(0);
const maxPages = ref<number>(1);

const headers = computed<DataTableHeader[]>(() => [
  {
    title: t('views.withdrawalList.header.username'),
    key: 'username',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.withdrawalList.header.mandateName'),
    key: 'mandateName',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.withdrawalList.header.userRib'),
    key: 'userRib',
    sortable: false,
    align: 'start'
  },
  {
    title: t('views.withdrawalList.header.amount'),
    key: 'amount',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.withdrawalList.header.createdAt'),
    key: 'createdAt',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.withdrawalList.header.status'),
    key: 'status',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.withdrawalList.header.actions'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  },
]);

onMounted(fetchWithdrawalOperations);

watch([search, sort, order, limit, page], fetchWithdrawalOperations);

function canUpdate(withdrawal: WithdrawalOperationResponse) {
  return withdrawal.status === WithdrawalStatusKey.PENDING;
}

async function fetchWithdrawalOperations(): Promise<void> {
  try {
    loading.value = true;

    const params: PaginatedQuery = {
      search: search.value,
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value
    };

    const response: PaginatedResponse<WithdrawalOperationResponse> =
      await BankService.getPaginatedWithdrawalOperations(params);

    withdrawalOperations.value = response.data;
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<WithdrawalOperationsSortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

async function onRowClick(_: MouseEvent, row: { item: WithdrawalOperationResponse }): Promise<void> {
  openWithdrawalDetail(row.item)
}

function openWithdrawalDetail(withdrawal: WithdrawalOperationResponse) {
  openDrawer({
    title: t('views.detailWithdrawal.title'),
    component: DetailWithdrawalView,
    props: {
      key: withdrawal.withdrawalId + '-' + Date.now(),
      withdrawalId: withdrawal.withdrawalId,
      drawerMode: true,
      onStatusUpdated: async () => {
        await fetchWithdrawalOperations()
        useDrawer().closeDrawer()
      }
    },
    width: 520,
  })
}

// --- Refuse withdrawal ---
async function refuseWithdrawal(withdrawal: WithdrawalOperationResponse) {
  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailWithdrawal.modal.refuse.title'),
      message: t('views.detailWithdrawal.modal.refuse.message'),
      isRefusal: true,
    },
  });

  if (modal && typeof modal === 'object') {
    try {
      loading.value = true;
      const { reason } = modal as WithdrawalRefuseRequest;

      await BankService.updateWithdrawalStatus(
        withdrawal.withdrawalId,
        WithdrawalStatusKey.REFUSED,
        reason
      );

      displayToast(t('views.detailWithdrawal.refuseSuccess'), 'success');
      withdrawal.status = WithdrawalStatusKey.REFUSED;
    } finally {
      loading.value = false;
    }
  }
}

// --- Validate withdrawal ---
async function validateWithdrawal(withdrawal: WithdrawalOperationResponse) {
  const modal = await displayModal({
    component: markRaw(OperationStatusComponent),
    props: {
      title: t('views.detailWithdrawal.modal.validate.title'),
      message: t('views.detailWithdrawal.modal.validate.message'),
    },
  });

  if (modal) {
    try {
      loading.value = true;

      await BankService.updateWithdrawalStatus(withdrawal.withdrawalId, WithdrawalStatusKey.VALIDATED);

      displayToast(t('views.detailWithdrawal.validatedSuccess'), 'success');
      withdrawal.status = WithdrawalStatusKey.VALIDATED;
    } finally {
      loading.value = false;
    }
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhArrowsLeftRight">
    {{ t("views.withdrawalList.title") }}
  </MainTitleComponent>

  <v-row>
    <v-col cols="12" sm="5">
      <v-text-field
        v-model="search"
        :label="t('views.withdrawalList.search')"
        append-inner-icon="PhMagnifyingGlass"
        density="compact"
        hide-details
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="withdrawalOperations"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        :sort-by="[{ key: sort, order: order }]"
        hover
        must-sort
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @update:sort-by="onSort"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.username`]="{ item }">
          <div class="d-flex flex-column align-items-center">
            {{ formatItemValue(item.username) }}
            <span class="text-caption text-medium-emphasis">
              {{ formatItemValue(item.email) }}
            </span>
          </div>
        </template>
        <template #[`item.mandateName`]="{ item }">
          {{ formatItemValue(item.mandateName) }}
        </template>
        <template #[`item.userRib`]="{ item }">
          {{ formatItemValue(item.userRib) }}
        </template>
        <template #[`item.amount`]="{ item }">
          {{ formatCurrency(item.amount) }}
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="formatWithdrawalStatus(item.status)"
            text-color="white"
            class="ma-2"
          >
            {{ t('views.withdrawalList.status.' + item.status) }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-menu>
            <template #activator="{ props }">
              <button type="button" v-bind="props">
                <v-icon icon="PhDotsThreeVertical" />
              </button>
            </template>

            <v-list>
              <v-list-item  @click.prevent="openWithdrawalDetail(item)">
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.withdrawalList.table.actions.detail') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhEye" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canUpdate(item)"
                @click.prevent="validateWithdrawal(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.withdrawalList.table.actions.validate') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhCheck" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canUpdate(item)"
                @click.prevent="refuseWithdrawal(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.withdrawalList.table.actions.cancel') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhX" />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
